import { createContext, Suspense, use, useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { useIntl } from 'react-intl'

import { ErrorBoundary, reloadPage } from '@/shared/layout/ErrorBoundary'
import { Skeleton } from '@/shared/layout/Skeleton'
import { cn, focusRing } from '@/shared/ui/cn'

import type { RegisteredAction, RegisteredColumn, RegisteredPanel, RegisteredTab } from './registry'
import { useSlot } from './registry-context'
import type { SlotContext } from './types'

/** Says a contribution could not be shown, without taking its host page down with it. */
function Unavailable() {
  const intl = useIntl()
  return (
    <span className="text-sm text-text-secondary">
      {intl.formatMessage({ id: 'shell.slots.unavailable' })}
    </span>
  )
}

/** A contribution that could not be shown, with a reload: React keeps a failed chunk. */
function UnavailableWithRetry() {
  const intl = useIntl()
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <Unavailable />
      <button
        type="button"
        onClick={reloadPage}
        className={cn(
          'cursor-pointer rounded text-sm font-medium text-primary-main underline',
          focusRing,
        )}
      >
        {intl.formatMessage({ id: 'shell.retry' })}
      </button>
    </span>
  )
}

/** How far a contribution has got: its code still loading, shown, or failed to load or render. */
export type SlotLoadState = 'loading' | 'shown' | 'failed'

/**
 * Hears each contribution inside it load, show or fail, by a key of its own, and `null` once it is
 * gone: for a host, such as a form, that must not act while one of its parts is missing.
 */
export const SlotLoadContext = createContext<((key: string, state: SlotLoadState | null) => void) | null>(null)

/**
 * Tells the `SlotLoadContext` the state its contribution has reached. It never clears it: the next
 * state replaces it, so the host sees no gap between loading and failing.
 */
function ReportLoad(props: { readonly loadKey: string; readonly state: SlotLoadState }) {
  const report = use(SlotLoadContext)
  const { loadKey, state } = props
  const reported = useRef(false)
  // Shown again after it suspended once more: only layout effects run again, and the
  // contribution's own effects ran when it first showed.
  useLayoutEffect(() => {
    if (reported.current) {
      report?.(loadKey, state)
    }
  }, [report, loadKey, state])
  useEffect(() => {
    reported.current = true
    report?.(loadKey, state)
  }, [report, loadKey, state])
  return null
}

/**
 * One contributed tab, panel or action, loading its own chunk behind a placeholder. If the chunk
 * fails to load or the contribution fails to render, it says so with a retry, and the host page
 * carries on. A column cell says only that it is not available, since a retry on every row
 * would repeat down the table.
 */
export function SlotContent(props: {
  readonly contribution: RegisteredTab | RegisteredPanel | RegisteredAction
  readonly context?: SlotContext | undefined
}) {
  const { Component, slot, id } = props.contribution
  const loadKey = useId()
  const report = use(SlotLoadContext)
  // A layout effect, so it runs before any state the parts below report in the same commit.
  useLayoutEffect(() => {
    if (report === null) {
      return
    }
    report(loadKey, 'loading')
    return () => report(loadKey, null)
  }, [report, loadKey])

  return (
    // A failure belongs to this contribution on this record, not to what shows here next.
    <ErrorBoundary
      fallback={
        <>
          <UnavailableWithRetry />
          <ReportLoad loadKey={loadKey} state="failed" />
        </>
      }
      resetKey={`${slot}\u0000${id}\u0000${props.context?.recordId ?? ''}`}
    >
      <Suspense
        fallback={
          <>
            <Skeleton className="h-10 w-full" />
            <ReportLoad loadKey={loadKey} state="loading" />
          </>
        }
      >
        <Component {...props.context} />
        {/* After the contribution, so its own effects, such as joining the form's save, have run. */}
        <ReportLoad loadKey={loadKey} state="shown" />
      </Suspense>
    </ErrorBoundary>
  )
}

/**
 * A contributed tab's counter, which draws nothing and reports its count to `onCount`, with the
 * tab's id. One that fails to load or render leaves the tab's label without a count.
 */
export function SlotCount(props: {
  readonly contribution: RegisteredTab
  readonly context?: SlotContext | undefined
  readonly onCount: (tabId: string, count: number | null, hasMore: boolean) => void
}) {
  const { Count, slot, id } = props.contribution
  const { onCount } = props
  const report = useCallback((count: number | null, hasMore = false) => onCount(id, count, hasMore), [onCount, id])
  if (Count === undefined) {
    return null
  }
  return (
    <ErrorBoundary fallback={null} resetKey={`${slot}\u0000${id}\u0000${props.context?.recordId ?? ''}`}>
      <Suspense fallback={null}>
        <Count {...props.context} onCount={report} />
      </Suspense>
    </ErrorBoundary>
  )
}

/** Every panel registered in `slot`, one after another. */
export function SlotPanels(props: {
  readonly slot: string
  readonly context?: SlotContext | undefined
  readonly className?: string | undefined
}) {
  const panels = useSlot(props.slot, 'panel')
  if (panels.length === 0) {
    return null
  }

  return (
    <div className={props.className}>
      {panels.map((panel) => (
        <SlotContent key={panel.id} contribution={panel} context={props.context} />
      ))}
    </div>
  )
}

/** Every action registered in `slot`, in a row. */
export function SlotActions(props: {
  readonly slot: string
  readonly context?: SlotContext | undefined
  readonly className?: string | undefined
}) {
  const actions = useSlot(props.slot, 'action')
  if (actions.length === 0) {
    return null
  }

  return (
    // Wraps, and keeps each action within its width so a long label wraps too: at 320 px or 200%
    // zoom every action stays in view rather than being cut off (WCAG 1.4.10).
    <div className={cn('flex min-w-0 max-w-full flex-wrap items-center gap-3 *:max-w-full', props.className)}>
      {actions.map((action) => (
        <SlotContent key={action.id} contribution={action} context={props.context} />
      ))}
    </div>
  )
}

type CellState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly value: unknown }
  | { readonly status: 'error' }

const loading: CellState = { status: 'loading' }

/** A contributed column as a host table draws it. */
export interface SlotColumn {
  readonly id: string
  readonly header: string
  /** One row's cell: a placeholder until its page's data arrives, then the contributor's cell. */
  readonly renderCell: (rowId: string) => ReactNode
}

/**
 * The columns other features registered in `slot`, with their data. Each column's `load` runs
 * once per page in `pages`, where a page is the row ids one load gave, kept as the same array
 * while it is shown. So a column costs one request per page of rows, never one per row. Only the
 * shown record's shown rows are kept: a host that moves to another record or drops a page lets
 * that data go, loads again if it comes back, and never shows another record's values.
 */
export function useSlotColumns(
  slot: string,
  pages: readonly (readonly string[])[],
  context: SlotContext = {},
): readonly SlotColumn[] {
  const intl = useIntl()
  const columns = useSlot(slot, 'column')
  const [cells, setCells] = useState<ReadonlyMap<string, CellState>>(() => new Map())
  const requested = useRef(new Map<readonly string[], Set<string>>())
  const latest = useRef(new Map<string, number>())
  const counter = useRef(0)
  const { recordId } = context

  useEffect(() => {
    const loadContext: SlotContext = recordId === undefined ? {} : { recordId }

    async function loadPage(column: RegisteredColumn, ids: readonly string[]) {
      // Numbered, so an older answer, or one for data let go, never overwrites what is shown.
      counter.current += 1
      const request = counter.current
      for (const id of ids) {
        latest.current.set(cellKey(recordId, column, id), request)
      }
      const apply = (state: (id: string) => CellState) => {
        setCells((current) => {
          // Cells no longer shown, whose numbers are gone, are let go here.
          const next = new Map([...current].filter(([key]) => latest.current.has(key)))
          for (const id of ids) {
            const key = cellKey(recordId, column, id)
            if (latest.current.get(key) === request) {
              next.set(key, state(id))
            }
          }
          return next
        })
      }

      apply(() => loading)
      try {
        const values = await column.load(ids, loadContext)
        apply((id) => ({ status: 'ready', value: values.get(id) }))
      } catch {
        apply(() => ({ status: 'error' }))
      }
    }

    // Keep only what is shown: its requests, its cells and their numbers.
    const shown = new Set<string>()
    const requests = new Map<readonly string[], Set<string>>()
    for (const page of pages) {
      const done = new Set(
        [...(requested.current.get(page) ?? [])].filter((marker) =>
          columns.some((column) => marker === cellKey(recordId, column, '')),
        ),
      )
      requests.set(page, done)
      for (const column of columns) {
        for (const id of page) {
          shown.add(cellKey(recordId, column, id))
        }
      }
    }
    requested.current = requests
    for (const key of latest.current.keys()) {
      if (!shown.has(key)) {
        latest.current.delete(key)
      }
    }
    // oxlint-disable-next-line react/set-state-in-effect -- lets go of rows no longer shown; the same map, and so no render, when there are none
    setCells((current) =>
      [...current.keys()].every((key) => shown.has(key))
        ? current
        : new Map([...current].filter(([key]) => shown.has(key))),
    )

    for (const page of pages) {
      const done = requests.get(page) ?? new Set<string>()
      for (const column of columns) {
        const marker = cellKey(recordId, column, '')
        if (!done.has(marker)) {
          done.add(marker)
          void loadPage(column, page)
        }
      }
    }
  }, [columns, pages, recordId])

  return columns.map((column) => ({
    id: column.id,
    header: intl.formatMessage({ id: column.headerId }),
    renderCell: (rowId: string) => {
      const state = cells.get(cellKey(recordId, column, rowId)) ?? loading
      const { Cell } = column
      const placeholder = <CellPlaceholder label={intl.formatMessage({ id: 'shell.loading' })} />
      switch (state.status) {
        case 'loading':
          return placeholder
        case 'error':
          return <Unavailable />
        case 'ready':
          return (
            <ErrorBoundary fallback={<Unavailable />} resetKey={cellKey(recordId, column, rowId)}>
              <Suspense fallback={placeholder}>
                <Cell rowId={rowId} value={state.value} />
              </Suspense>
            </ErrorBoundary>
          )
      }
    },
  }))
}

/** A cell's key: its record, its column's slot and id, and its row. */
function cellKey(recordId: string | undefined, column: RegisteredColumn, rowId: string): string {
  return [recordId ?? '', column.slot, column.id, rowId].join('\u0000')
}

function CellPlaceholder(props: { readonly label: string }) {
  return (
    <span className="inline-flex">
      <span className="sr-only">{props.label}</span>
      <Skeleton className="h-4 w-16" />
    </span>
  )
}
